import { useState, useEffect } from 'react';
import { useTenant } from '../contexts/TenantContext';
import { useDashboardData } from '../contexts/DashboardDataContext';
import {
  Settings, 
  Building2,
  Clock,
  Save,
  AlertCircle,
  CheckCircle,
  Loader2,
  CreditCard,
  Calendar,
  TrendingUp,
  ExternalLink,
  RefreshCw,
  User,
  Info,
  Mail,
  Trash2,
  Plus,
  Phone
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export default function SettingsPage() {
  const { getToken } = useTenant();
  const dashData = useDashboardData();
  const preloadedSettings = dashData?.settings;
  const preloadedTimezones = dashData?.timezones;
  const preloadedSubscription = dashData?.walletStatus;
  const hasPreloaded = !!(preloadedSettings && preloadedTimezones);
  const [settings, setSettings] = useState(preloadedSettings || null);
  const [timezones, setTimezones] = useState(preloadedTimezones?.timezones || []);
  const [subscription, setSubscription] = useState(preloadedSubscription || null);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(!hasPreloaded);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [activeTab, setActiveTab] = useState('profile');
  const [checkoutLoading, setCheckoutLoading] = useState(null); // Track specific plan being loaded
  // Email sender identity state
  const [emailIdentities, setEmailIdentities] = useState([]);
  const [newSenderEmail, setNewSenderEmail] = useState('');
  const [verifyingSender, setVerifyingSender] = useState(false);
  const [refreshingSenders, setRefreshingSenders] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    address_line1: '',
    address_line2: '',
    city: '',
    state: '',
    postal_code: '',
    timezone: '',
    quiet_hours_start: '',
    quiet_hours_end: '',
    contact_first_name: '',
    contact_last_name: '',
    contact_email: '',
    contact_mobile: ''
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = await getToken();
        const headers = {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        };

        // Use preloaded data if available, only fetch what's missing
        let settingsData = preloadedSettings;
        if (!settingsData) {
          const settingsRes = await fetch(`${BACKEND_URL}/api/settings`, { headers });
          if (!settingsRes.ok) throw new Error('Failed to fetch settings');
          settingsData = await settingsRes.json();
          setSettings(settingsData);
        }

        setFormData({
          name: settingsData.name || '',
          phone: settingsData.phone || '',
          address_line1: settingsData.address_line1 || '',
          address_line2: settingsData.address_line2 || '',
          city: settingsData.city || '',
          state: settingsData.state || '',
          postal_code: settingsData.postal_code || '',
          timezone: settingsData.timezone || 'Australia/Sydney',
          quiet_hours_start: settingsData.quiet_hours_start || '20:00',
          quiet_hours_end: settingsData.quiet_hours_end || '09:00',
          contact_first_name: settingsData.contact_first_name || '',
          contact_last_name: settingsData.contact_last_name || '',
          contact_email: settingsData.contact_email || '',
          contact_mobile: settingsData.contact_mobile || ''
        });

        if (!preloadedTimezones) {
          const timezonesRes = await fetch(`${BACKEND_URL}/api/settings/timezones`, { headers });
          if (timezonesRes.ok) {
            const tzData = await timezonesRes.json();
            setTimezones(tzData.timezones);
          }
        }

        if (!preloadedSubscription) {
          const subscriptionRes = await fetch(`${BACKEND_URL}/api/stripe/subscription-status`, { headers });
          if (subscriptionRes.ok) setSubscription(await subscriptionRes.json());
        }

        // Plans always fetched (not preloaded, public endpoint)
        const plansRes = await fetch(`${BACKEND_URL}/api/stripe/plans`);
        if (plansRes.ok) {
          const plansData = await plansRes.json();
          setPlans(plansData.plans || []);
        }

        // Fetch email sender identities
        try {
          const sendersRes = await fetch(`${BACKEND_URL}/api/email-sender/identities`, { headers });
          if (sendersRes.ok) {
            const data = await sendersRes.json();
            setEmailIdentities(data.identities || []);
          }
        } catch (e) {
          // Non-critical — email senders may not be set up yet
        }

        // Check for subscription success/cancel or tab param from URL
        const params = new URLSearchParams(window.location.search);
        if (params.get('tab') === 'email') {
          setActiveTab('email');
          window.history.replaceState({}, '', '/dashboard/settings');
        } else if (params.get('subscription') === 'success') {
          setSuccess('Subscription activated successfully!');
          setActiveTab('billing');
          window.history.replaceState({}, '', '/dashboard/settings');
        } else if (params.get('subscription') === 'cancelled') {
          setActiveTab('billing');
          window.history.replaceState({}, '', '/dashboard/settings');
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getToken]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      const token = await getToken();
      const response = await fetch(`${BACKEND_URL}/api/settings`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.detail || 'Failed to save settings');
      }

      const data = await response.json();
      setSettings(data);
      setSuccess('Settings saved successfully');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleSubscribe = async (planCode, billingInterval) => {
    const loadingKey = `${planCode}-${billingInterval}`;
    setCheckoutLoading(loadingKey);
    setError(null);

    try {
      const token = await getToken();
      const response = await fetch(`${BACKEND_URL}/api/stripe/create-checkout-session`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          plan_code: planCode,
          billing_interval: billingInterval,
        }),
      });

      if (!response.ok) {
        let errorMessage = 'Failed to create checkout session';
        try {
            const err = await response.json();
            errorMessage = err.detail || errorMessage;
        } catch (e) {
            // Body might be locked or empty
            errorMessage = `Error ${response.status}: ${response.statusText}`;
        }
        throw new Error(errorMessage);
      }

      const data = await response.json();
      window.location.href = data.checkout_url;
    } catch (err) {
      console.error("Subscription error:", err);
      setError(err.message);
      setCheckoutLoading(null);
    }
  };

  const handleManageSubscription = async () => {
    setCheckoutLoading('portal');
    setError(null);

    try {
      const token = await getToken();
      const response = await fetch(`${BACKEND_URL}/api/stripe/create-portal-session`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.detail || 'Failed to open billing portal');
      }

      const data = await response.json();
      window.location.href = data.portal_url;
    } catch (err) {
      setError(err.message);
      setCheckoutLoading(null);
    }
  };

  const handleCancelSubscription = async () => {
    if (!window.confirm('Are you sure you want to cancel your subscription? You will retain access until the end of your current billing period.')) {
      return;
    }

    setCheckoutLoading('cancel');
    setError(null);

    try {
      const token = await getToken();
      const response = await fetch(`${BACKEND_URL}/api/stripe/cancel-subscription`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.detail || 'Failed to cancel subscription');
      }

      // Refresh subscription status
      const subRes = await fetch(`${BACKEND_URL}/api/stripe/subscription-status`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      if (subRes.ok) {
        setSubscription(await subRes.json());
      }

      setSuccess('Subscription will be cancelled at the end of the billing period');
    } catch (err) {
      setError(err.message);
    } finally {
      setCheckoutLoading(null);
    }
  };

  const handleReactivate = async () => {
    setCheckoutLoading('reactivate');
    setError(null);

    try {
      const token = await getToken();
      const response = await fetch(`${BACKEND_URL}/api/stripe/reactivate-subscription`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.detail || 'Failed to reactivate subscription');
      }

      // Refresh subscription status
      const subRes = await fetch(`${BACKEND_URL}/api/stripe/subscription-status`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      if (subRes.ok) {
        setSubscription(await subRes.json());
      }

      setSuccess('Subscription reactivated successfully');
    } catch (err) {
      setError(err.message);
    } finally {
      setCheckoutLoading(null);
    }
  };

  if (loading) {
    return (
      <div className="p-6 lg:p-8">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-gray-200 rounded w-1/4"></div>
          <div className="bg-gray-200 rounded-xl h-96"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8" data-testid="settings-page">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Settings</h1>
        <p className="text-gray-600 mt-1">Manage your business profile, billing, and SMS settings</p>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6 border-b border-gray-200">
        <button
          onClick={() => setActiveTab('profile')}
          className={`px-4 py-2 font-medium text-sm border-b-2 -mb-px transition-colors ${
            activeTab === 'profile'
              ? 'border-orange-500 text-orange-600'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          Business Profile
        </button>
        <button
          onClick={() => setActiveTab('billing')}
          className={`px-4 py-2 font-medium text-sm border-b-2 -mb-px transition-colors ${
            activeTab === 'billing'
              ? 'border-orange-500 text-orange-600'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
          data-testid="billing-tab"
        >
          SMS Credits & Billing
        </button>
        <button
          onClick={() => setActiveTab('email')}
          className={`px-4 py-2 font-medium text-sm border-b-2 -mb-px transition-colors ${
            activeTab === 'email'
              ? 'border-orange-500 text-orange-600'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          Email Senders
        </button>
      </div>

      {/* Alerts */}
      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
          <p className="text-red-700">{error}</p>
        </div>
      )}

      {success && (
        <div className="mb-6 bg-green-50 border border-green-200 rounded-lg p-4 flex items-center gap-3">
          <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
          <p className="text-green-700">{success}</p>
        </div>
      )}

      {activeTab === 'profile' && (
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Business Owner/Contact */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-100 flex items-center gap-3">
              <User className="w-5 h-5 text-gray-400" />
              <h2 className="text-lg font-semibold text-gray-900">Business Owner</h2>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">First Name</label>
                  <input
                    type="text"
                    value={formData.contact_first_name}
                    onChange={(e) => setFormData({ ...formData, contact_first_name: e.target.value })}
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                    placeholder="Owner's first name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Last Name</label>
                  <input
                    type="text"
                    value={formData.contact_last_name}
                    onChange={(e) => setFormData({ ...formData, contact_last_name: e.target.value })}
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                    placeholder="Owner's last name"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email Address</label>
                <input
                  type="email"
                  value={formData.contact_email}
                  onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                  placeholder="contact@business.com"
                />
                <p className="text-xs text-gray-500 mt-1">This email is used for login and important notifications.</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Mobile Number</label>
                <input
                  type="tel"
                  value={formData.contact_mobile}
                  onChange={(e) => setFormData({ ...formData, contact_mobile: e.target.value })}
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                  placeholder="+61400000000"
                />
                <p className="text-xs text-gray-500 mt-1">Used to verify campaign sends via OTP. Include country code (e.g. +61).</p>
              </div>
            </div>
          </div>

          {/* Business Profile */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-100 flex items-center gap-3">
              <Building2 className="w-5 h-5 text-gray-400" />
              <h2 className="text-lg font-semibold text-gray-900">Business Details</h2>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Business Name</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                  data-testid="business-name-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number</label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Address Line 1</label>
                  <input
                    type="text"
                    value={formData.address_line1}
                    onChange={(e) => setFormData({ ...formData, address_line1: e.target.value })}
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Address Line 2</label>
                  <input
                    type="text"
                    value={formData.address_line2}
                    onChange={(e) => setFormData({ ...formData, address_line2: e.target.value })}
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
                  <input
                    type="text"
                    value={formData.city}
                    onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">State</label>
                  <input
                    type="text"
                    value={formData.state}
                    onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Postal Code</label>
                  <input
                    type="text"
                    value={formData.postal_code}
                    onChange={(e) => setFormData({ ...formData, postal_code: e.target.value })}
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Timezone & Quiet Hours */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-100 flex items-center gap-3">
              <Clock className="w-5 h-5 text-gray-400" />
              <h2 className="text-lg font-semibold text-gray-900">Timezone & Quiet Hours</h2>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Timezone</label>
                <select
                  value={formData.timezone}
                  onChange={(e) => setFormData({ ...formData, timezone: e.target.value })}
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                  data-testid="timezone-select"
                >
                  {timezones.map((tz) => (
                    <option key={tz.value} value={tz.value}>
                      {tz.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="text-sm font-medium text-gray-900 mb-2">Quiet Hours</h3>
                <p className="text-sm text-gray-600 mb-4">
                  SMS messages will not be sent during quiet hours. This is enforced server-side for compliance.
                </p>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Start Time</label>
                    <input
                      type="time"
                      value={formData.quiet_hours_start}
                      onChange={(e) => setFormData({ ...formData, quiet_hours_start: e.target.value })}
                      className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                      data-testid="quiet-hours-start"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">End Time</label>
                    <input
                      type="time"
                      value={formData.quiet_hours_end}
                      onChange={(e) => setFormData({ ...formData, quiet_hours_end: e.target.value })}
                      className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                      data-testid="quiet-hours-end"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* KYC Status */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-100">
              <h2 className="text-lg font-semibold text-gray-900">Compliance Status</h2>
            </div>
            <div className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-medium text-gray-900">KYC Verification</h3>
                  <p className="text-sm text-gray-500">Business verification status</p>
                </div>
                {settings?.kyc_verified ? (
                  <span className="px-3 py-1 bg-green-100 text-green-700 text-sm font-medium rounded-full flex items-center gap-1">
                    <CheckCircle className="w-4 h-4" />
                    Verified
                  </span>
                ) : (
                  <span className="px-3 py-1 bg-yellow-100 text-yellow-700 text-sm font-medium rounded-full">
                    Pending
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Save Button */}
          <div className="flex justify-end">
            <button
              type="submit"
              disabled={saving}
              className="flex items-center gap-2 px-6 py-2.5 bg-orange-500 text-white rounded-lg hover:bg-orange-600 disabled:opacity-50 transition-colors"
              data-testid="save-settings-btn"
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  Save Settings
                </>
              )}
            </button>
          </div>
        </form>
      )}

      {activeTab === 'billing' && (
        <div className="space-y-6">
          {/* Wallet Balance - Primary Section */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-100 flex items-center gap-3">
              <CreditCard className="w-5 h-5 text-gray-400" />
              <h2 className="text-lg font-semibold text-gray-900">Wallet Balance</h2>
            </div>
            <div className="p-6">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
                <div>
                  <div className="text-sm text-gray-500 mb-1">Current Balance</div>
                  <div className="text-4xl font-bold text-gray-900">
                    {subscription?.wallet_balance_dollars || '$0.00'}
                  </div>
                  <div className="text-sm text-gray-500 mt-2">
                    ≈ {(subscription?.sms_capacity || 0).toLocaleString()} SMS remaining
                  </div>
                  <div className="text-xs text-gray-400 mt-1">
                    @ ${((subscription?.sms_cost_cents || 6.5) / 100).toFixed(3)} per SMS
                  </div>
                </div>
                <div className="flex flex-col sm:flex-row gap-3">
                  <a
                    href="/dashboard/wallet"
                    className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-orange-500 text-white rounded-lg hover:bg-orange-600 font-medium"
                  >
                    <TrendingUp className="w-4 h-4" />
                    Top Up Wallet
                  </a>
                  <a
                    href="/dashboard/wallet"
                    className="inline-flex items-center justify-center gap-2 px-6 py-3 border border-gray-200 text-gray-700 rounded-lg hover:bg-gray-50 font-medium"
                  >
                    View Transactions
                  </a>
                </div>
              </div>
              
              {/* Info Banner */}
              <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex items-start gap-3">
                  <Info className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-blue-800">How billing works</p>
                    <p className="text-sm text-blue-600 mt-1">
                      DAZZA uses a simple wallet system. Top up your balance anytime and use it to send SMS campaigns. 
                      The more you send, the cheaper each SMS becomes. Unused balance never expires.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* SMS Pricing */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-100">
              <h2 className="text-lg font-semibold text-gray-900">SMS Pricing</h2>
              <p className="text-sm text-gray-500 mt-1">
                Volume discounts applied automatically as you send more
              </p>
            </div>
            <div className="p-6">
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                <div className="p-4 bg-gray-50 rounded-lg text-center">
                  <div className="text-lg font-bold text-gray-900">$0.07</div>
                  <div className="text-xs text-gray-500">0 - 999 SMS</div>
                </div>
                <div className="p-4 bg-gray-50 rounded-lg text-center">
                  <div className="text-lg font-bold text-gray-900">$0.06</div>
                  <div className="text-xs text-gray-500">1K - 4,999 SMS</div>
                </div>
                <div className="p-4 bg-green-50 rounded-lg text-center border border-green-200">
                  <div className="text-lg font-bold text-green-700">$0.05</div>
                  <div className="text-xs text-green-600">5K - 19,999 SMS</div>
                  <div className="text-[10px] text-green-500 mt-1">Save 29%</div>
                </div>
                <div className="p-4 bg-green-50 rounded-lg text-center border border-green-200">
                  <div className="text-lg font-bold text-green-700">$0.04</div>
                  <div className="text-xs text-green-600">20K - 99,999 SMS</div>
                  <div className="text-[10px] text-green-500 mt-1">Save 43%</div>
                </div>
                <div className="p-4 bg-orange-50 rounded-lg text-center border border-orange-200">
                  <div className="text-lg font-bold text-orange-700">$0.03</div>
                  <div className="text-xs text-orange-600">100K+ SMS</div>
                  <div className="text-[10px] text-orange-500 mt-1">Best value</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'email' && (
        <div className="space-y-6">
          {/* Email Sender Identities */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Mail className="w-5 h-5 text-gray-400" />
                <h2 className="text-lg font-semibold text-gray-900">Verified Sender Emails</h2>
              </div>
              <button
                onClick={async () => {
                  setRefreshingSenders(true);
                  try {
                    const token = await getToken();
                    const res = await fetch(`${BACKEND_URL}/api/email-sender/identities`, {
                      headers: { 'Authorization': `Bearer ${token}` },
                    });
                    if (res.ok) {
                      const data = await res.json();
                      setEmailIdentities(data.identities || []);
                    }
                  } catch (e) {
                    console.error(e);
                  } finally {
                    setRefreshingSenders(false);
                  }
                }}
                className="text-sm text-gray-500 hover:text-gray-700 flex items-center gap-1"
                disabled={refreshingSenders}
              >
                <RefreshCw className={`w-4 h-4 ${refreshingSenders ? 'animate-spin' : ''}`} />
                Refresh
              </button>
            </div>
            <div className="p-6 space-y-4">
              <p className="text-sm text-gray-600">
                You must verify a sender email address before sending email campaigns.
                We'll send a verification email — click the link in it to verify.
              </p>

              {/* Add new sender */}
              <div className="flex gap-2">
                <input
                  type="email"
                  value={newSenderEmail}
                  onChange={(e) => setNewSenderEmail(e.target.value)}
                  placeholder="hello@yourbusiness.com"
                  className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      document.getElementById('verify-sender-btn')?.click();
                    }
                  }}
                />
                <button
                  id="verify-sender-btn"
                  onClick={async () => {
                    if (!newSenderEmail.trim()) return;
                    setVerifyingSender(true);
                    setError(null);
                    try {
                      const token = await getToken();
                      const res = await fetch(`${BACKEND_URL}/api/email-sender/verify`, {
                        method: 'POST',
                        headers: {
                          'Authorization': `Bearer ${token}`,
                          'Content-Type': 'application/json',
                        },
                        body: JSON.stringify({ email: newSenderEmail.trim() }),
                      });
                      const data = await res.json();
                      if (!res.ok) {
                        setError(data.detail || 'Failed to start verification');
                      } else {
                        setSuccess(data.message || 'Verification email sent!');
                        setNewSenderEmail('');
                        setTimeout(() => setSuccess(null), 5000);
                        // Refresh list
                        const listRes = await fetch(`${BACKEND_URL}/api/email-sender/identities`, {
                          headers: { 'Authorization': `Bearer ${token}` },
                        });
                        if (listRes.ok) {
                          const listData = await listRes.json();
                          setEmailIdentities(listData.identities || []);
                        }
                      }
                    } catch (e) {
                      setError(e.message);
                    } finally {
                      setVerifyingSender(false);
                    }
                  }}
                  disabled={verifyingSender || !newSenderEmail.trim()}
                  className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-orange-500 rounded-lg hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {verifyingSender ? (
                    <><Loader2 className="w-4 h-4 animate-spin" /> Verifying...</>
                  ) : (
                    <><Plus className="w-4 h-4" /> Verify Email</>
                  )}
                </button>
              </div>

              {/* Identity list */}
              {emailIdentities.length > 0 ? (
                <div className="divide-y divide-gray-100 border border-gray-100 rounded-lg">
                  {emailIdentities.map((identity) => (
                    <div key={identity.id} className="flex items-center justify-between px-4 py-3">
                      <div className="flex items-center gap-3">
                        <Mail className="w-4 h-4 text-gray-400" />
                        <span className="text-sm font-medium text-gray-900">{identity.identity_value}</span>
                        {identity.verified ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium bg-green-50 text-green-700 rounded-full">
                            <CheckCircle className="w-3 h-3" /> Verified
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium bg-yellow-50 text-yellow-700 rounded-full">
                            <Loader2 className="w-3 h-3" /> Pending
                          </span>
                        )}
                      </div>
                      <button
                        onClick={async () => {
                          if (!window.confirm(`Remove ${identity.identity_value}?`)) return;
                          try {
                            const token = await getToken();
                            await fetch(`${BACKEND_URL}/api/email-sender/identities/${identity.id}`, {
                              method: 'DELETE',
                              headers: { 'Authorization': `Bearer ${token}` },
                            });
                            setEmailIdentities(prev => prev.filter(i => i.id !== identity.id));
                          } catch (e) {
                            console.error(e);
                          }
                        }}
                        className="p-1.5 text-gray-400 hover:text-red-500 transition-colors rounded"
                        title="Remove"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-400 text-sm">
                  No sender emails configured yet. Add one above to get started.
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
